/**
 * 成就通知组件（Task 3）
 *
 * 战斗完成后显示成就解锁动画
 * 设计约束：Ardot v2.0（无圆角、1-2px 描边、monospace 字体）
 */

export interface AchievementData {
  title: string;           // "新成就解锁"
  levelName: string;       // 关卡名称
  score: "S" | "A" | "B" | "C";
  difficulty: number;      // 1-5
  timeUsed: number;        // 战斗耗时（ticks）
}

/**
 * 显示成就通知动画
 *
 * @param container - 容器元素
 * @param data - 成就数据
 * @returns Promise，动画完成后 resolve
 *
 * 动画流程：
 * - 进入：从下方滑入 + 淡入（0.3s）
 * - 停留：2s
 * - 退出：淡出（0.3s）
 * - 总时长：2.6s
 */
export function showAchievementNotification(
  container: HTMLElement,
  data: AchievementData
): Promise<void> {
  return new Promise((resolve) => {
    // 创建通知 DOM
    const notification = document.createElement("div");
    notification.className = "achievement-notification";
    notification.innerHTML = `
      <div class="achievement-notification-content">
        <div class="achievement-notification-header">
          <span class="achievement-notification-title">${data.title}</span>
        </div>
        <div class="achievement-notification-body">
          <div class="achievement-notification-level">${data.levelName}</div>
          <div class="achievement-notification-stats">
            <span class="achievement-notification-score achievement-score-${data.score}">${data.score}</span>
            <span class="achievement-notification-difficulty">难度 ${data.difficulty}</span>
            <span class="achievement-notification-time">${formatTicks(data.timeUsed)}</span>
          </div>
        </div>
      </div>
    `;

    // 添加到容器
    container.appendChild(notification);

    // 触发进入动画（0.3s）
    requestAnimationFrame(() => {
      notification.classList.add("achievement-notification--enter");
    });

    // 停留 2s 后触发退出动画
    const exitTimer = setTimeout(() => {
      notification.classList.remove("achievement-notification--enter");
      notification.classList.add("achievement-notification--exit");

      // 退出动画完成后（0.3s）移除 DOM 并 resolve
      const removeTimer = setTimeout(() => {
        notification.remove();
        resolve();
      }, 300);

      return () => clearTimeout(removeTimer);
    }, 2000);

    return () => clearTimeout(exitTimer);
  });
}

/**
 * 格式化 ticks 为可读时间
 * @param ticks - ticks 数量
 * @returns 格式化字符串（如 "30s"）
 */
function formatTicks(ticks: number): string {
  const seconds = Math.round(ticks / 30); // 30Hz
  return `${seconds}s`;
}
